import { Box, styled } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useEffect, useMemo, useState } from "react";
import { DrawerTriggerButton } from "@/components/DrawerTriggerButton/DrawerTriggerButton";
import { DateSlider } from "@/components/dateSlider";
import { SelfSchedulingButton } from "@/components/ImportSelfSchedulingButton/SelfSchedulingButton";
import { DRAWER_WIDTH_IN_PX } from "@/components/RecommendationsDrawer/utils";
import { ContractSectionsDropdown } from "@/components/team/ContractSectionsDropdown";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useDrawerTriggerNotification } from "@/contexts/RecommendationsDrawerContext";
import { useRequestsContext } from "@/contexts/RequestsContext";
import { useWeekViewContext } from "@/contexts/WeekView/WeekViewContext";
import { weekPersonIdsOf } from "@/contexts/WeekView/weekPersonIds";
import { useFormatPersonName } from "@/hooks/useNameFormat";
import { CONTROL_HEIGHT, INK_BLACK, NEUTRAL, SURFACE } from "@/theme/tokens";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "@/ui/Inputs/SystemAutoComplete/SystemAutoComplete";
import { countRequestsWithResponses } from "@/utils/utilFunctions";
import { Availability } from "../Availability";
import { DraftMode } from "../DraftMode";
import { ScheduleExportButton } from "../ScheduleExport/ScheduleExportButton";
import { ScheduleVisibilityButton } from "../Settings/ScheduleVisibilityButton";
import { StaffingSettingsPanel } from "../Settings/StaffingSettings";
import { GlobalLocationOrder } from "./GlobalLocationOrder";

interface IProps {
	drawerOpen: boolean;
	weekIndex: number;
	setSearchText: (val: SystemAutoCompleteOption<string> | null) => void;
	searchText: SystemAutoCompleteOption<string> | null;
	handleDrawerOpen: () => void;
	setAvailabilityLoaded: (availabilityLoaded: boolean) => void;
	onStaffScheduleVisibilityClick?: () => void;
	selectedContractSections: ReadonlySet<string>;
	onContractSectionsChange: (sections: string[]) => void;
}

const TopBar = styled("div")({
	display: "flex",
	alignItems: "center",
	gap: "1px",
	backgroundColor: SURFACE,
	borderBottom: `1px solid ${NEUTRAL[300]}`,
});

const TopRightPane = styled("div")(({ theme }) => ({
	marginLeft: "auto",
	display: "flex",
	alignItems: "center",
	gap: theme.spacing(0.5),
	minWidth: "fit-content",
}));

export const StaffingHeader: FC<IProps> = ({
	drawerOpen,
	weekIndex,
	searchText,
	setSearchText,
	handleDrawerOpen,
	setAvailabilityLoaded,
	onStaffScheduleVisibilityClick,
	selectedContractSections,
	onContractSectionsChange,
}) => {
	const { isHospital } = useHospitalData();
	const { visibleWeekViewData, getSchedulePerson } = useWeekViewContext();
	const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);
	const { staffRequests } = useRequestsContext();
	const { getDrawerTriggerNotification } = useDrawerTriggerNotification();
	const requestsWithResponsesCount = useMemo(() => countRequestsWithResponses(staffRequests), [staffRequests]);
	const notification = getDrawerTriggerNotification(requestsWithResponsesCount);

	const formatPersonName = useFormatPersonName();
	// The shown week's people, so the search is there as soon as the week is, without the staff list.
	const nursesNamesList = useMemo<SystemAutoCompleteOption<string>[]>(() => {
		return [...weekPersonIdsOf(visibleWeekViewData)]
			.flatMap((nurseId) => {
				const person = getSchedulePerson(nurseId);
				if (!person) return [];
				const name = formatPersonName(person);
				return [{ label: name, value: name }];
			})
			.sort((option, otherOption) => option.label.localeCompare(otherOption.label));
	}, [visibleWeekViewData, getSchedulePerson, formatPersonName]);

	useEffect(() => {
		const handleResize = () => {
			setIsSmallScreen(window.innerWidth < 1420); // Adjust this value as needed
		};

		window.addEventListener("resize", handleResize);
		handleResize(); // Call once to set initial state

		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return (
		<TopBar
			sx={(theme) => ({
				borderBottom: `1px solid ${NEUTRAL[300]}`,
				background: alpha(theme.palette.primary.light, 0.08),
				position: "fixed",
				overflow: "visible",
				top: "var(--app-header-height, 64px)",
				left: 0,
				right: drawerOpen ? DRAWER_WIDTH_IN_PX : 0,
				zIndex: 999,
				height: "78px",
				width: "auto",
				boxShadow: `0px 0px 10px 0px ${alpha(INK_BLACK, 0.1)}`,
				padding: "6px 12px",
				transition: theme.transitions.create(["width", "right"], {
					easing: theme.transitions.easing.sharp,
					duration: theme.transitions.duration.leavingScreen,
				}),
			})}
		>
			<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
				<DateSlider isWeekControl />

				{/* Paired with the v1 grid's `StaffAutoComplete`: mutually exclusive branches of the same
				    route, so one anchor name covers both. */}
				<SystemAutoComplete
					data-annie-target="staffing-staff-search"
					options={nursesNamesList}
					value={searchText}
					onChange={(_, value) => setSearchText(value || null)}
					textFieldProps={{
						placeholder: "Type First / Last Name",
						label: "Search Staff",
						disabled: false,
						inputSx: {
							width: "300px",
							// 36px here is what put this control below every other field on the row:
							// its neighbours render the standard height, and a shorter input inside an
							// equally tall wrapper resolves its own centre lower.
							height: CONTROL_HEIGHT.STANDARD,
						},
						reserveHeightForHelperText: false,
					}}
					disabled={false}
				/>
				<ContractSectionsDropdown
					dataTestId="weekly-gaps-contract-sections-dropdown"
					selectedSections={selectedContractSections}
					onChange={onContractSectionsChange}
				/>
			</Box>
			{isHospital && <Availability isSmall={isSmallScreen} setAvailabilityLoaded={setAvailabilityLoaded} />}
			<TopRightPane>
				<DraftMode weekIndex={weekIndex} />
				<SelfSchedulingButton />
				{onStaffScheduleVisibilityClick && (
					<ScheduleVisibilityButton dataTestId="staffing-header-schedule-visibility-button" onClick={onStaffScheduleVisibilityClick} />
				)}
				{/* Anchored only on the two weekly-Staffing branches: the Day Schedule and the People view render
				    this same panel, and an anchor there would spotlight it on pages the step's copy isn't about. */}
				<StaffingSettingsPanel data-annie-target="staffing-view-settings" mode="weekly" />
				<GlobalLocationOrder />

				<ScheduleExportButton weekIndex={weekIndex} />

				{/* Anchored only on the two weekly-Staffing branches: the Day Schedule and Synergy Staffing render
				    this same button, and an anchor there would spotlight it on pages the step's copy isn't about. */}
				<DrawerTriggerButton
					open={drawerOpen}
					notification={notification}
					onClick={handleDrawerOpen}
					data-testid="copilot-drawer-button"
					data-annie-target="staffing-recommendations"
				/>
			</TopRightPane>
		</TopBar>
	);
};
